import React, { useState, useEffect, useRef } from 'react';

const FloatingAnnotationForm = ({
  onSubmit,
  onCancel,
  isVisible = false,
  selectedMessageCount = 0
}) => {
  const [annotationTitle, setAnnotationTitle] = useState('');
  const [annotationContent, setAnnotationContent] = useState('');
  const [annotationType, setAnnotationType] = useState('improvement');
  const [color, setColor] = useState('#FEF3C7');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  const annotationTypes = [
    { value: 'improvement', label: 'Improvement', color: '#D1FAE5', description: 'Suggestions for better processes or features' },
    { value: 'bug', label: 'Bug', color: '#FEE2E2', description: 'Issues or problems that need fixing' },
    { value: 'important', label: 'Important', color: '#FEF3C7', description: 'Critical information or key points' },
    { value: 'other', label: 'Other', color: '#E0E7FF', description: 'General notes or miscellaneous items' }
  ];

  const handleTypeChange = (type) => {
    setAnnotationType(type);
    const typeConfig = annotationTypes.find(t => t.value === type);
    if (typeConfig && type !== 'custom') {
      setColor(typeConfig.color);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!annotationTitle.trim()) {
      alert('Please enter an annotation title');
      return;
    }

    if (selectedMessageCount === 0) {
      alert('Please select at least one message to annotate');
      return;
    }

    setIsSubmitting(true);
    try {
      const annotationData = {
        annotation_title: annotationTitle.trim(),
        annotation_content: annotationContent.trim() || null,
        annotation_type: annotationType,
        color: color,
        scope: 'concierge'
      };

      await onSubmit(annotationData);

      // Reset form
      setAnnotationTitle('');
      setAnnotationContent('');
      setAnnotationType('important');
      setColor('#FEF3C7');
    } catch (error) {
      console.error('Error in floating annotation form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setAnnotationTitle('');
    setAnnotationContent('');
    setAnnotationType('important');
    setColor('#FEF3C7');
    onCancel();
  };

  // Handle escape key to close form
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVisible) {
        handleCancel();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isVisible]);

  // Handle click outside to close form
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (formRef.current && !formRef.current.contains(e.target) && isVisible) {
        handleCancel();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible]);

  if (!isVisible) return null;

    return (
    <div
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
      style={{ pointerEvents: 'auto' }}
    >
      <div
        ref={formRef}
        className="bg-white rounded-lg shadow-2xl border border-gray-200 w-full max-w-md mx-4 transform transition-all duration-200 scale-100"
      >
        {/* Form header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center">
              <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
              Create Annotation
            </h3>
            <button
              onClick={handleCancel}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form content */}
        <div className="p-6">
          {selectedMessageCount === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">Select Messages to Annotate</h3>
              <p className="text-sm text-gray-500 mb-4">
                Click on messages to select a range for annotation
              </p>
              <div className="text-xs text-gray-400">
                💡 <strong>Tip:</strong> Click the first message, then click the last message to select all messages in between
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={annotationTitle}
                onChange={(e) => setAnnotationTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                placeholder="Enter annotation title"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Annotation Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                {annotationTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`relative flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                      annotationType === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="annotationType"
                      value={type.value}
                      checked={annotationType === type.value}
                      onChange={() => handleTypeChange(type.value)}
                      className="sr-only"
                    />
                    <div className="flex items-center space-x-3 w-full">
                      <div
                        className="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-200"
                        style={{
                          borderColor: annotationType === type.value ? '#3B82F6' : '#D1D5DB',
                          backgroundColor: annotationType === type.value ? '#3B82F6' : 'transparent'
                        }}
                      >
                        {annotationType === type.value && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 flex-1">
                        <div
                          className="w-4 h-4 rounded border"
                          style={{ backgroundColor: type.color }}
                        ></div>
                        <span className="text-sm font-medium text-gray-700">
                          {type.label}
                        </span>
                      </div>
                    </div>
                    {annotationType === type.value && (
                      <div className="absolute top-2 right-2">
                        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-10 h-10 border border-gray-300 rounded cursor-pointer hover:scale-110 transition-transform duration-200"
                />
                <span className="text-xs text-gray-500 font-mono">{color}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={annotationContent}
                onChange={(e) => setAnnotationContent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                placeholder="Additional notes (optional)"
                rows={3}
              />
            </div>



            <div className="flex space-x-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting || !annotationTitle.trim() || selectedMessageCount === 0}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 transform hover:scale-105 active:scale-95"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating...
                  </span>
                ) : (
                  'Create Annotation'
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium transition-all duration-200 transform hover:scale-105 active:scale-95"
              >
                Cancel
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FloatingAnnotationForm;
